import React, { FunctionComponent, ReactNode } from 'react';
import type {
  ControllerFieldState,
  ControllerRenderProps,
  UseControllerProps,
  UseFormReturn,
  UseFormStateReturn,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';
import classNames from 'classnames';
import i18next from 'i18next';
import { WarningOutlined } from '@ant-design/icons';
import { ErrorMessage } from '@hookform/error-message';
import { Flex, TextComponent, TextComponentProps } from '../ui-kit';
import { FieldLabel } from './field-label';

import './form-field.scss';

function isFieldRequired(rules?: { required?: unknown }): boolean {
  const r = rules?.required;
  if (r === undefined || r === null) return false;
  if (typeof r === 'boolean') return r;
  if (typeof r === 'object' && r !== null && 'value' in r) {
    return !!(r as { value?: boolean }).value;
  }
  return true;
}

export interface FormFieldProps {
  text?: string | ReactNode;
  textLevel?: TextComponentProps['level'];
  requiredSymbolColor?: string;
  className?: string;
  placeholder?: string;
  form?: UseFormReturn<any>;
  mode?: 'multiple' | 'tags' | undefined;
  controllerProps: UseControllerProps;
  showErrorMessage?: boolean;
  render: ({
    field,
    fieldState,
    formState,
  }: {
    field: ControllerRenderProps<any>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<any>;
  }) => React.ReactElement;
}

export const FormField: FunctionComponent<
  Omit<FormFieldProps, 'placeholder'>
> = ({
  form,
  controllerProps,
  text,
  textLevel,
  requiredSymbolColor,
  className,
  showErrorMessage = true,
  render,
}) => {
  const required = isFieldRequired(controllerProps?.rules);
  if (controllerProps?.rules?.required) {
    controllerProps.rules.required = i18next.t('validations:missing_field');
  }

  return (
    <Flex vertical className={classNames('form-field', className)}>
      {text && (
        <FieldLabel
          isRequired={required}
          color={requiredSymbolColor}
          level={textLevel}
        >
          {text}
        </FieldLabel>
      )}
      <Controller
        {...controllerProps}
        name={controllerProps.name}
        control={form?.control}
        render={render}
      />
      {showErrorMessage ? (
        <ErrorMessage
          render={({ message }) => (
            <TextComponent level={7} className="error-message">
              <WarningOutlined />
              {message}
            </TextComponent>
          )}
          errors={form?.formState.errors}
          name={controllerProps.name}
        />
      ) : null}
    </Flex>
  );
};
